import { Menu, MenuItem } from '@szhsin/react-menu';
import styled, { createGlobalStyle } from 'styled-components';

import { ModelInfo } from '#/api/terminal/types';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  if (models.length === 0) return null;
  const selected = models.find((m) => m.id === selectedId) ?? models[0];
  if (selected == null) return null;
  return (
    <>
      <ModelPickerMenuStyles />
      <Menu
        menuButton={
          <Trigger type="button" aria-label={`Model: ${selected.label}`}>
            <TriggerLabel>{selected.label}</TriggerLabel>
          </Trigger>
        }
        align="end"
        direction="top"
        transition
        menuClassName="terminal-model-picker-menu"
      >
        <Heading>Models</Heading>
        {models.map((m) => (
          <MenuItem
            key={m.id}
            onClick={() => onSelect(m.id)}
            className="terminal-model-picker-item"
          >
            <ItemRow>
              <ItemLabel>
                {m.label}
                {m.legacy === true && <LegacyTag>Legacy</LegacyTag>}
              </ItemLabel>
              <CheckSlot>{m.id === selected.id && <CheckIcon />}</CheckSlot>
            </ItemRow>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12L10 17L20 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Trigger = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    color: var(--text-primary);
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

// Match the folder action menu (RecentsView.tsx) so the two dropdowns
// in the Block Terminal sidebar look identical. `@szhsin/react-menu`
// renders the dropdown into a portal, so the menu element isn't a
// child of a styled-components wrapper — global selectors scoped to
// the opted-in class names (`menuClassName` + item `className`) are
// the cleanest fit. The selectors are name-prefixed so they can't
// bleed into any other react-menu instance in the app.
const ModelPickerMenuStyles = createGlobalStyle`
  .terminal-model-picker-menu {
    /* Solid color rather than --bg-color-container so the menu pops
       over the surrounding sidebar instead of blending into it — same
       reasoning as the folder context menu in RecentsView. */
    background: #1a2030;
    border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
    border-radius: 8px;
    padding: 4px;
    min-width: 160px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
    /* Reset any default library padding so the items align flush with
       the 4px container padding. */
    margin: 0;
    list-style: none;
  }

  .terminal-model-picker-item {
    padding: 6px 8px;
    border-radius: 4px;
    color: #fff;
    font-size: 0.75rem;
    line-height: 1.125rem;
    cursor: pointer;
    background: transparent;

    &.szh-menu__item--hover,
    &.szh-menu__item--focusable:hover,
    &:hover {
      background: var(--hover-bg, rgba(255, 255, 255, 0.08));
      color: #fff;
    }
  }
`;

const Heading = styled.div`
  /* Match the folder menu items' horizontal padding (6px 8px) so the
     "Models" label aligns with the model names below it. */
  padding: 4px 8px 6px;
  font-size: 0.7rem;
  color: var(--text-secondary);
`;

const ItemRow = styled.span`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-secondary);
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;
